import styled from 'styled-components';
import { media } from '../../styles/breakpoints';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 4px;
  border-radius: 4px;
  background: var(--spark-color-surface);
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;

  // На мобильном увеличиваем touch-target (28px меньше рекомендованных 44px).
  ${media.mobile`
    width: 36px;
    height: 36px;
  `}

  border: 1px solid ${(p) => {
    if (p.$danger) return 'var(--spark-color-error-text)';
    if (p.$deleted === true) return 'var(--spark-color-control-neutral)';
    if (p.$deleted === false) return 'var(--spark-color-success-text)';
    return 'var(--spark-color-control-border)';
  }};

  color: ${(p) => {
    if (p.$danger) return 'var(--spark-color-error-text)';
    if (p.$deleted === true) return 'var(--spark-color-control-disabled-text)';
    if (p.$deleted === false) return 'var(--spark-color-success-text)';
    return 'var(--spark-color-text-secondary)';
  }};

  &:hover {
    background: ${(p) => {
      if (p.$danger) return 'var(--spark-color-error-subtle-surface)';
      if (p.$deleted === true) return 'var(--spark-color-success-surface)';
      if (p.$deleted === false) return 'var(--spark-color-control-disabled-surface)';
      return 'var(--spark-color-control-hover-surface)';
    }};
    border-color: ${(p) => {
      if (p.$danger) return 'var(--spark-color-error-text)';
      if (p.$deleted === true) return 'var(--spark-color-success-text)';
      if (p.$deleted === false) return 'var(--spark-color-control-neutral)';
      return 'var(--spark-color-control-neutral)';
    }};
  }

  svg {
    width: 16px;
    height: 16px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PenIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function BanIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 21V9" /><path d="m7 14 5-5 5 5" /><path d="M5 3h14" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 11a8 8 0 1 0 1 4" /><path d="M20 4v7h-7" />
    </svg>
  );
}

function TransferIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7h13" /><path d="m12 3 4 4-4 4" /><path d="M21 17H8" /><path d="m12 13-4 4 4 4" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

const ICONS = {
  trash: TrashIcon,
  plus: PlusIcon,
  pen: PenIcon,
  lock: BanIcon,
  unlock: CheckCircleIcon,
  ban: BanIcon,
  checkCircle: CheckCircleIcon,
  download: DownloadIcon,
  upload: UploadIcon,
  transfer: TransferIcon,
  refresh: RefreshIcon,
};

export default function IconButton({ icon, $danger, $deleted, onClick, title, style, disabled, type = 'button', ariaLabel }) {
  const IconComponent = ICONS[icon];
  if (!IconComponent) return null;

  return (
    <StyledButton type={type} aria-label={ariaLabel || title} $danger={$danger} $deleted={$deleted} onClick={onClick} title={title} style={style} disabled={disabled}>
      <IconComponent />
    </StyledButton>
  );
}
